import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Clock, Search, Check, CalendarDays } from 'lucide-react';
import { WishlistButton } from '../components/WishlistButton';
import { useWishlist } from '../hooks/useWishlist';
import { api } from '../api';
import { Price } from '../context/CurrencyContext';
import { useLang } from '../context/LanguageContext';

const Packages = ({ user }) => {
  const navigate = useNavigate();
  const { t, loc } = useLang();
  const wishlist = useWishlist(user);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    api.get('/packages')
      .then((res) => setPackages(res.data))
      .catch((err) => console.error('Failed to load packages', err))
      .finally(() => setLoading(false));
  }, []);

  const filteredPackages = packages.filter((pkg) =>
    pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pkg.destinations.some(dest => dest.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleBookPackage = (pkg) => {
    navigate('/booking', { state: { package: pkg } });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="packages-page">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Travel Packages</h1>
          <p className="text-xl text-teal-50">All-inclusive packages for your perfect vacation</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="package-search-input"
              type="text"
              placeholder="Search packages by name or destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 py-6 text-lg border-2 border-gray-200 focus:border-teal-500 rounded-xl"
            />
          </div>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <p data-testid="packages-loading" className="text-center text-gray-500 py-12">Loading packages...</p>
        ) : filteredPackages.length === 0 ? (
          <div className="text-center py-12">
            <p data-testid="packages-empty" className="text-gray-500 text-lg">No packages found. Try a different search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="packages-grid">
            {filteredPackages.map((pkg) => (
              <Card
                key={pkg.id}
                data-testid={`package-card-${pkg.id}`}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={pkg.image}
                    alt={pkg.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <WishlistButton active={wishlist.has('package', pkg.id)} onClick={() => wishlist.toggle('package', pkg.id)} testId={`wishlist-package-${pkg.id}`} />
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{loc(pkg, 'name')}</h3>
                  
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center text-teal-600 font-semibold text-xl">
                      <Price inr={pkg.price} />
                      <span className="text-sm text-gray-500 ml-2">per person</span>
                    </div>
                    <div className="flex items-center text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{pkg.duration}</span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Package Includes:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {pkg.includes.map((item, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-600">
                          <Check className="w-4 h-4 text-teal-600 mr-2" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Highlights:</h4>
                    <div className="flex flex-wrap gap-2">
                      {pkg.highlights.map((highlight, idx) => (
                        <span key={idx} className="bg-teal-50 text-teal-700 px-3 py-1 rounded-full text-sm">
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-2">
                    <Button
                      data-testid={`package-itinerary-${pkg.id}`}
                      variant="outline"
                      onClick={() => navigate(`/packages/${pkg.id}`)}
                      className="flex-1 border-2 border-teal-500 text-teal-600 hover:bg-teal-50"
                    >
                      <CalendarDays className="w-4 h-4 mr-2" />
                      {t('viewItinerary')}
                    </Button>
                    <Button
                      data-testid={`package-book-${pkg.id}`}
                      onClick={() => handleBookPackage(pkg)}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white"
                    >
                      {t('bookPackage')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Packages;
